/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ControlValueAccessor, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

// Base class for a form component that can be reordered and managed with add & delete
// the class has 2 overrideable methods
// createBlankItem(): TModel
// createFormGroupForRowItem(row: TModel): FormGroup
@Component({
  template: '',
  standalone: false,
})
export abstract class FormAddListBase<TModel> implements ControlValueAccessor, OnInit {
  cannotDeleteMessage = 'This tab cannot be deleted as it is has children';
  confirmDelete = false;
  entityDescription: string = 'item';

  dataForm: FormGroup;
  dragDisabled = true;
  propagateChange: any = () => {};
  onTouched: any = () => {};

  nextId = -1; // counter for new items
  myWrittenValue: any[] = []; // stores the value set on this form control
  addToEndOfList: boolean = true;

  get itemArray(): FormArray {
    return this.dataForm.controls['itemArray'] as FormArray;
  }

  constructor(protected fb: FormBuilder, protected formHelperService: ReactiveFormHelperService, protected snackBar: SnackBarService, protected dialog: MatDialog) {
    this.dataForm = fb.group({
      itemArray: fb.array([]),
    });
  }

  ngOnInit(): void {
    // when the value of the form changes then the changes should be propagated to the parent
    this.dataForm.valueChanges.subscribe((e) => {
      this.onFormChange();
    });
  }

  private onFormChange() {
    this.myWrittenValue = this.itemArray.value; //update the value locally so that it is always current
    this.propagateChange(this.myWrittenValue);
    this.onTouched();
  }

  // called when the host form sets the value on the control
  writeValue(value: any): void {
    this.myWrittenValue = value === '' ? [] : value;
    this.setControlsFromWrittenValue();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public validate() {
    const isNotValid = !this.dataForm.valid;
    return (
      isNotValid && {
        invalid: true,
      }
    );
  }

  // TODO: [Technical Debt][dpb][2022-12-29] Should probably only be called when the parent sets the value.
  // However reordering (drag and drop) affects the structure and can't be applied to the controls array directly
  setControlsFromWrittenValue() {
    this.itemArray.controls = [];

    if (this.myWrittenValue?.length === 0) {
      this.itemArray.setValue([]);
    }

    this.myWrittenValue?.map((item) => {
      this.itemArray.push(this.createFormGroupForRowItem(item));
    });
    this.propagateChange(this.itemArray.value);
  }

  onListDrop(event) {
    // Return the drag container to disabled.
    this.dragDisabled = true;

    const previousIndex = this.itemArray.value.findIndex((d) => d.id === +event.item.data.controls['id'].value);

    moveItemInArray(this.myWrittenValue, previousIndex, event.currentIndex);

    this.myWrittenValue.forEach((value, index) => {
      value.ordinal = index + 1;
    });

    this.setControlsFromWrittenValue();
  }

  markControlsAsTouched() {
    this.formHelperService.markFormControlTouched(this.dataForm);
  }

  get listHasItems(): boolean {
    return this.itemArray.value?.length > 0;
  }

  addRow() {
    const row: TModel = this.createBlankItem();

    if (this.addToEndOfList) {
      this.itemArray.push(this.createFormGroupForRowItem(row));
    } else {
      this.itemArray.insert(0, this.createFormGroupForRowItem(row));
    }
  }

  // Create a new item for the row in the form control
  protected abstract createBlankItem(): TModel;

  // Create a new form group item for the row in the form control
  protected abstract createFormGroupForRowItem(row: TModel): FormGroup;

  removeRow(indexToRemove: number) {
    let existingItem = this.itemArray.value?.[indexToRemove];
    if (existingItem.canDelete) {
      if (this.confirmDelete) {
        // show confirmation
        const dialogRef = this.dialog.open(ConfirmationModalComponent, {
          width: '450px',
          data: {
            title: 'Confirm Delete',
            message: `Are you sure you want to delete this ${this.entityDescription}?`,
          },
        });

        dialogRef.componentInstance.onClose.subscribe((result) => {
          if (result.result) {
            this.removeItem(indexToRemove);
          }
        });
      } else {
        this.removeItem(indexToRemove);
      }
    } else {
      this.snackBar.info(this.cannotDeleteMessage);
    }
  }

  private removeItem(indexToRemove: number) {
    this.itemArray.controls.splice(indexToRemove, 1);
    this.itemArray.updateValueAndValidity();
  }

  isControlInvalid(fieldName: string, validationRule) {
    var invalidControls = this.itemArray.controls.filter((x) => x.invalid);

    if (invalidControls.length === 0) return false;

    var isInValid = false;
    invalidControls.forEach((element) => {
      var control = element.get(fieldName);
      if (control.hasError(validationRule) && control.touched) {
        isInValid = true;
      }
    });

    return isInValid;
  }
}
